import React, {
  useCallback, useEffect, useRef, useState
}  from 'react';

import flatten from 'lodash/flatten';

import { scaleBand, scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { select } from 'd3-selection';

import Axis from '../axis/D3Axis';
import ChartGrid from '../axis/D3ChartGrid';
import ClipSection from '../utility/D3ClipPath';
import MouseoverTooltip from '../utility/D3MouseoverTooltip';

import MultiBars from './D3MultiBars';
import { roundedAxisValuesByRange } from '../d3-helpers';

const getAllYValues = (datasets = []) => flatten(datasets.map(({ data }) => data.map(({ y }) => y)))
const getAllXValues = (datasets = []) => flatten(datasets.map(({ data }) => data.map(({ x }) => x)))

const DEFAULT_MARGINS = {
  top: 20, right: 20, bottom: 100, left: 40
};

const BarChartAxis = ({
  chartId,
  xScale,
  yScale,
  xTicks,
  yTicks,
  yTickValues,
  xAxisLabel,
  yAxisLabel,
  xTickFormat,
  yTickFormat,
  xTickWidth,
  margins = DEFAULT_MARGINS,
  height,
  width,
  clipPathId,
  labelRotation = -45,
}) => {
  const xSettings = {
    id: `${chartId}-x-axis`,
    scale: xScale,
    orient: 'bottom',
    transform: `translate(0, ${height})`,
    clipPath: `url(#${clipPathId})`,
    labelRotation,
    axisLabel: xAxisLabel,
    ticks: xTicks,
    height,
    width,
    margins,
    tickFormat: xTickFormat,
    tickWidth: xTickWidth,
    useTickWrapping: true,
  };
  const ySettings = {
    id: `${chartId}-y-axis`,
    scale: yScale,
    orient: 'left',
    transform: 'translate(0, 0)',
    axisLabel: yAxisLabel,
    ticks: yTicks,
    tickFormat: yTickFormat,
    tickValues: yTickValues,
    height,
    width,
    margins,
  };

  return (
    <g>
      <Axis {...xSettings} />
      <Axis {...ySettings} />
    </g>
  )
}

export const D3MultiBarChart = ({
  datasets,
  numberOfXTicks,
  numberOfYTicks,
  xAxisLabel,
  yAxisLabel,
  xTickFormat,
  yTickFormat,
  renderTooltip,
  containerRef,
  yAxisPadding = 1.2,
  margins = DEFAULT_MARGINS,
  chartId = 'bar-chart',
  labelRotation = -45,
  containerWidth = 800,
  containerHeight = 300,
  showGrid = true,
  stacked = false,
}) => {
  const allYValues = getAllYValues(datasets);
  const allXValues = getAllXValues(datasets);
  const allDatasetIds = datasets.map(({ id }) => id);
  
  const clipPathId = `${chartId}-clip-path`;
  const [tooltipPoint, setTooltipPoint] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);
  const chartRef = useRef(null)

  const width = containerWidth > 0 ? containerWidth - margins.left - margins.right : 0;
  const height = containerHeight > 0 ? containerHeight - margins.top - margins.bottom : 0;

  const maxY = max(allYValues, val => val);

  // Consider basing padding and step sizing off of rounded square root / power of 2 values instead
  const paddedMaxY = ((Math.round(maxY / 10) * 10) || maxY) * yAxisPadding;

  // Add X axis
  const xScale = scaleBand()
    .domain(allXValues)
    .range([0, width])
    .padding([0.2])

  // Another scale for subgroup position?
  const xSubgroup = scaleBand()
    .domain(allDatasetIds)
    .range([0, xScale.bandwidth()])
    .padding([0.05])

  // Add Y axis
  const yScale = scaleLinear()
    .domain([0, paddedMaxY])
    .range([height, 0])
    .nice();

  const onItemHover = useCallback((item, mousePointer) => {
    setTooltipPoint(item);

    if (mousePointer) {
      setMousePosition({ x: mousePointer[0], y: mousePointer[1] });
    } else {
      setMousePosition(null);
    }
  }, [])

  useEffect(() => {
    let svg = select(chartRef.current).select('svg')

    // On first render creates the svg
    if (svg.empty()) {
      svg = select(chartRef.current)
        .append('svg')
        .attr('width', width)
        .attr('height', height);
    }
  }, [height, width])

  return (
    <>
      <svg
        ref={chartRef}
        id={chartId}
        width={width + margins.left + margins.right}
        height={height + margins.top + margins.bottom}
      >
        <ClipSection
          id={clipPathId}
          x={0}
          y={0}
          height={height}
          width={width}
        />
        <g
          transform={`translate(${margins.left}, ${margins.top})`}
        >
          {
            showGrid && (
              <ChartGrid
                chartId={chartId}
                clipPathId={clipPathId}
                xScale={xScale}
                yScale={yScale}
                xTicks={numberOfXTicks}
                yTicks={numberOfYTicks}
                // yTickValues={yTickValues}
                height={height}
                width={width}
                verticalGridOffset={xScale.bandwidth() * 0.63}
              />
            )
          }
          <BarChartAxis
            margins={margins}
            chartId={chartId}
            clipPathId={clipPathId}
            xAxisLabel={xAxisLabel}
            yAxisLabel={yAxisLabel}
            xScale={xScale}
            yScale={yScale}
            xTicks={numberOfXTicks}
            yTicks={numberOfYTicks}
            // yTickValues={yTickValues}
            xTickWidth={xScale.bandwidth()}
            xTickFormat={xTickFormat}
            yTickFormat={yTickFormat}
            height={height}
            width={width}
            labelRotation={labelRotation}
          />
          <g clipPath={`url(#${clipPathId})`}>
            <MultiBars
              id={`${chartId}-multi-bars`}
              containerRef={containerRef}
              datasets={datasets}
              labelScale={xSubgroup}
              groupScale={xScale}
              yScale={yScale}
              height={height}
              onItemHover={onItemHover}
              stacked={stacked}
            />
          </g>
        </g>
      </svg>
      <MouseoverTooltip
        id={`${chartId}-tooltip`}
        xScale={xSubgroup}
        yScale={yScale}
        tooltipPoint={tooltipPoint}
        mousePosition={mousePosition}
        useMousePosition
      >
        { renderTooltip && renderTooltip(tooltipPoint) }
      </MouseoverTooltip>
    </>
  )
};

export default D3MultiBarChart;
